import React, { useEffect, useMemo } from "react";
import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import {
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfFooter,
  FrVrfFormPanel,
} from "@vrf/fr-vrf-web-components-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import { createModal } from "../../modal";
import { ApplicationMessageScreen } from "../../../messages/screen-message";
import {
  UpdateProjectRequest,
  useUpdateProject,
  ComponentProject,
} from "../../../services/project";
import { useGetIFDefinitions } from "../../../services/if-definitions";
import {
  GetCpntPjtCpntKey,
  useGetComponentProjectList,
} from "../../../services/component-projects";
import { useGetGeneralCodes } from "../../../services/data-list";
import {
  useLinkComponentProjectDialogState,
  SpreadsheetRecordData,
  CellItem,
  LinkComponentProjectDialogInitialState,
  RefSpreadsheetRecordData,
  SpreadsheetMessage,
  Option,
} from ".";
import { usePageNavigation } from "../../../pages/use-page-navigation";
import { ENV } from "@vrf/fr-vrf-core";
import _ from "lodash";
import { Cell } from "@vrf/fr-vrf-spreadsheet-core-templating";

export enum LocalIOTypeLabel {
  I = "In",
  O = "Out",
  IO = "InOut",
}

const MANUAL_LINK = {
  tooltip: "7. Design IF Requirements",
  value:
    "https://confluence.fastretailing.com/pages/viewpage.action?pageId=817541410",
};

const REF_TEMPLATE_FILE_URL = `spreadsheet-template/project-plan-summary-of-related-component-list.xlsx`;
const TEMPLATE_FILE_URL = `spreadsheet-template/project-plan-linked-component-project-list.xlsx`;

export const LinkComponentProjectDialogContent: React.FC = () => {
  const { showToast, showConfirmationDialog, resetUserError } =
    useGlobalNotifier();
  const { messageForScreen } = useMessage();
  const [updateProjectResponse, updateProjectRequest] = useUpdateProject();
  const [pageState, setPageState] = useLinkComponentProjectDialogState();
  const [refSpreadsheetState, setRefSpreadsheetState, initRefSpreadsheetState] =
    useSpreadsheetState<{
      refSpreadSheet: RefSpreadsheetRecordData[];
    }>();
  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<{
      spreadSheet: SpreadsheetRecordData[];
    }>();
  const [getIFDefinitionsResponse, getIFDefinitionsRequest] =
    useGetIFDefinitions();
  const { moveToModalPage, closeModalPage } = usePageNavigation();
  const [getComponentProjectListResponse, getComponentProjectListRequest] =
    useGetComponentProjectList();
  const [getGeneralCodesResponse, getGeneralCodesRequest] =
    useGetGeneralCodes();
  const closeModal = (isSaved: boolean) => {
    if (!isSaved) {
      resetUserError();
      setPageState(LinkComponentProjectDialogInitialState);
      return;
    }
    closeModalPage();

    setPageState((prevState) => ({
      ...prevState,
      isShow: false,
      status: {
        ...prevState.status,
        isSaved: true,
      },
    }));
  };

  const onInitialDisplayed = (): void => {
    moveToModalPage();
    resetUserError();
    getIFDefinitionsRequest({
      dfnSetVerNum: ENV("REACT_APP_DFN_SET_VER_NUM"),
      project: { pjtIds: [pageState.pjtId] },
    });
    const cdTypes = ["chn_type_type", "edh_notification_type"];
    getGeneralCodesRequest({ cdTypes: cdTypes });
  };
  useEffect(onInitialDisplayed, []);

  const onListenGetGeneralCodesResponse = () => {
    let chnTypeType = [] as Option[];
    let edhNotificationType = [] as Option[];
    const generalCode = getGeneralCodesResponse?.gnrlCdTypeList ?? [];

    generalCode.forEach((e) => {
      switch (e.cdType) {
        case "chn_type_type":
          chnTypeType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
        case "edh_notification_type":
          edhNotificationType = e.cdList.map((e) => {
            return {
              label: e.label,
              value: e.value,
            };
          });
          break;
      }
    });

    setPageState((prevState) => ({
      ...prevState,
      codes: {
        ...prevState.codes,
        chnTypeType: chnTypeType,
        edhNotificationType: edhNotificationType,
      },
    }));
  };
  useEffect(onListenGetGeneralCodesResponse, [getGeneralCodesResponse]);

  const onListenGetIFDefinitionsResponse = (): void => {
    if (!getIFDefinitionsResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      ifDefinitionResult: getIFDefinitionsResponse,
    }));
  };
  useEffect(onListenGetIFDefinitionsResponse, [getIFDefinitionsResponse]);

  const requestGetComponentProject = (): void => {
    resetUserError();
    const cpntList = [] as GetCpntPjtCpntKey[];
    getIFDefinitionsResponse?.ifList.forEach((e) => {
      if (
        e.src.interfaceDefinition.inbound?.componentId &&
        e.src.interfaceDefinition.dipByRegion
      ) {
        cpntList.push({
          cpntId: e.src.interfaceDefinition.inbound?.componentId,
          dipByRgn: e.src.interfaceDefinition.dipByRegion,
        });
      }
      if (
        e.dst.interfaceDefinition.outbound?.componentId &&
        e.dst.interfaceDefinition.dipByRegion
      ) {
        cpntList.push({
          cpntId: e.dst.interfaceDefinition.outbound?.componentId,
          dipByRgn: e.dst.interfaceDefinition.dipByRegion,
        });
      }
    });
    pageState.edited.componentRequirementList?.forEach((e) => {
      if (e.cpntId && e.dipByRgn) {
        cpntList.push({ cpntId: e.cpntId, dipByRgn: e.dipByRgn });
      }
    });
    if (cpntList.length !== 0) {
      getComponentProjectListRequest({
        cpntList: cpntList,
      });
    }
  };
  useEffect(requestGetComponentProject, [
    getIFDefinitionsResponse,
    pageState.edited.componentRequirementList,
  ]);

  const onListenGetComponentProjectResponse = (): void => {
    if (!getComponentProjectListResponse) return;

    setPageState((prevState) => ({
      ...prevState,
      componentProjectResult: getComponentProjectListResponse,
    }));
  };
  useEffect(onListenGetComponentProjectResponse, [
    getComponentProjectListResponse,
  ]);

  const refSpreadsheetInitialData = useMemo(() => {
    const refSpreadSheetMap = new Map<string, RefSpreadsheetRecordData[]>();
    pageState.ifDefinitionResult.ifList.forEach((e) => {
      const srcKey = `${e.src.interfaceDefinition.dipByRegion ?? ""}${
        e.src.interfaceDefinition.inbound?.componentId ?? ""
      }`;
      const srcChainTypeCode =
        pageState.codes.chnTypeType?.find(
          (itm) =>
            itm.value ===
            e.src.interfaceDefinition.inbound?.inboundPartner?.chainTypeCode,
        )?.label || "";
      const dstChainTypeCode =
        pageState.codes.chnTypeType?.find(
          (itm) =>
            itm.value ===
            e.dst.interfaceDefinition.outbound?.outboundPartner?.chainTypeCode,
        )?.label || "";
      const srcNotificationType =
        pageState.codes.edhNotificationType?.find(
          (itm) =>
            itm.value ===
            e.src.interfaceDefinition.inbound?.inboundNotification
              ?.notificationType,
        )?.label || "";
      const dstNotificationType =
        pageState.codes.edhNotificationType?.find(
          (itm) =>
            itm.value ===
            e.dst.interfaceDefinition.outbound?.outboundNotification
              ?.notificationType,
        )?.label || "";
      const srcData: RefSpreadsheetRecordData = {
        dipByRgn: e.src.interfaceDefinition.dipByRegion ?? "",
        cpntId: e.src.interfaceDefinition.inbound?.componentId ?? "",
        cpntReqDesc: "",
        inOutTypeCd: LocalIOTypeLabel.I,
        edhPrtcls: e.src.interfaceDefinition.inbound?.protocol
          ? [`${e.src.interfaceDefinition.inbound?.protocol}`]
          : [],
        ediPrtnrs:
          e.src.interfaceDefinition.inbound?.inboundPartner?.partnerCode &&
          e.src.interfaceDefinition.inbound?.inboundPartner?.chainTypeCode
            ? [
                `${
                  e.src.interfaceDefinition.inbound?.inboundPartner
                    ?.partnerCode ?? ""
                }:${srcChainTypeCode}`,
              ]
            : [],
        edhNotifications: e.src.interfaceDefinition.inbound?.inboundNotification
          ?.notificationType
          ? [`${srcNotificationType}`]
          : [],
      };
      if (refSpreadSheetMap.has(srcKey)) {
        refSpreadSheetMap.get(srcKey)?.push(srcData);
      } else {
        refSpreadSheetMap.set(srcKey, [srcData]);
      }
      const dstKey = `${e.dst.interfaceDefinition.dipByRegion ?? ""}${
        e.dst.interfaceDefinition.outbound?.componentId ?? ""
      }`;
      const dstData: RefSpreadsheetRecordData = {
        dipByRgn: e.dst.interfaceDefinition.dipByRegion ?? "",
        cpntId: e.dst.interfaceDefinition.outbound?.componentId ?? "",
        cpntReqDesc: "",
        inOutTypeCd: LocalIOTypeLabel.O,
        edhPrtcls: e.dst.interfaceDefinition.outbound?.protocol
          ? [`${e.dst.interfaceDefinition.outbound?.protocol}`]
          : [],
        ediPrtnrs:
          e.dst.interfaceDefinition.outbound?.outboundPartner?.partnerCode &&
          e.dst.interfaceDefinition.outbound?.outboundPartner.chainTypeCode
            ? [
                `${
                  e.dst.interfaceDefinition.outbound?.outboundPartner
                    ?.partnerCode ?? ""
                }:${dstChainTypeCode}`,
              ]
            : [],
        edhNotifications: e.dst.interfaceDefinition.outbound
          ?.outboundNotification?.notificationType
          ? [`${dstNotificationType}`]
          : [],
      };
      if (refSpreadSheetMap.has(dstKey)) {
        refSpreadSheetMap.get(dstKey)?.push(dstData);
      } else {
        refSpreadSheetMap.set(dstKey, [dstData]);
      }
    });

    pageState.edited.componentRequirementList?.forEach((e) => {
      const reqKey = `${e.dipByRgn}${e.cpntId}`;
      const reqData: RefSpreadsheetRecordData = {
        dipByRgn: e.dipByRgn,
        cpntId: e.cpntId,
        cpntReqDesc: e.cpntReqDesc,
        inOutTypeCd: "",
        edhPrtcls: [],
        ediPrtnrs: [],
        edhNotifications: [],
      };
      if (refSpreadSheetMap.has(reqKey)) {
        refSpreadSheetMap.get(reqKey)?.push(reqData);
      } else {
        refSpreadSheetMap.set(reqKey, [reqData]);
      }
    });
    const refSpreadsheetRecordData = [] as RefSpreadsheetRecordData[];
    refSpreadSheetMap.forEach((value, key) => {
      const result: RefSpreadsheetRecordData = {
        cpntId: "",
        dipByRgn: "",
        cpntReqDesc: "",
        inOutTypeCd: "",
        edhPrtcls: [],
        ediPrtnrs: [],
        edhNotifications: [],
      };
      const edhProtcolMap = new Map<string, string>();
      const ediProtcolMap = new Map<string, string>();
      const edhNotificationMap = new Map<string, string>();
      value.forEach((e) => {
        result.cpntId = e.cpntId;
        result.dipByRgn = e.dipByRgn;
        if (e.cpntReqDesc !== "") {
          result.cpntReqDesc = e.cpntReqDesc;
        }
        if (e.edhPrtcls.length) {
          if (
            edhProtcolMap.has(`${e.dipByRgn}_${e.cpntId}_${e.edhPrtcls}`) &&
            (edhProtcolMap.get(`${e.dipByRgn}_${e.cpntId}_${e.edhPrtcls}`) ===
              `${LocalIOTypeLabel.IO} ${e.edhPrtcls}` ||
              edhProtcolMap.get(`${e.dipByRgn}_${e.cpntId}_${e.edhPrtcls}`) !==
                `${e.inOutTypeCd} ${e.edhPrtcls}`)
          ) {
            edhProtcolMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.edhPrtcls}`,
              `${LocalIOTypeLabel.IO} ${e.edhPrtcls}`,
            );
          } else {
            edhProtcolMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.edhPrtcls}`,
              `${e.inOutTypeCd} ${e.edhPrtcls}`,
            );
          }
        }
        if (e.ediPrtnrs.length) {
          if (
            ediProtcolMap.has(`${e.dipByRgn}_${e.cpntId}_${e.ediPrtnrs}`) &&
            (ediProtcolMap.get(`${e.dipByRgn}_${e.cpntId}_${e.ediPrtnrs}`) ===
              `${LocalIOTypeLabel.IO} ${e.ediPrtnrs}` ||
              ediProtcolMap.get(`${e.dipByRgn}_${e.cpntId}_${e.ediPrtnrs}`) !==
                `${e.inOutTypeCd} ${e.ediPrtnrs}`)
          ) {
            ediProtcolMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.ediPrtnrs}`,
              `${LocalIOTypeLabel.IO} ${e.ediPrtnrs}`,
            );
          } else {
            ediProtcolMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.ediPrtnrs}`,
              `${e.inOutTypeCd} ${e.ediPrtnrs}`,
            );
          }
        }
        if (e.edhNotifications.length) {
          if (
            edhNotificationMap.has(
              `${e.dipByRgn}_${e.cpntId}_${e.edhNotifications}`,
            ) &&
            (edhNotificationMap.get(
              `${e.dipByRgn}_${e.cpntId}_${e.edhNotifications}`,
            ) === `${LocalIOTypeLabel.IO} ${e.edhNotifications}` ||
              edhNotificationMap.get(
                `${e.dipByRgn}_${e.cpntId}_${e.edhNotifications}`,
              ) !== `${e.inOutTypeCd} ${e.edhNotifications}`)
          ) {
            edhNotificationMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.edhNotifications}`,
              `${LocalIOTypeLabel.IO} ${e.edhNotifications}`,
            );
          } else {
            edhNotificationMap.set(
              `${e.dipByRgn}_${e.cpntId}_${e.edhNotifications}`,
              `${e.inOutTypeCd} ${e.edhNotifications}`,
            );
          }
        }
      });
      edhProtcolMap.forEach((value, key) => {
        result.edhPrtcls.push(value);
      });
      ediProtcolMap.forEach((value, key) => {
        result.ediPrtnrs.push(value);
      });
      edhNotificationMap.forEach((value, key) => {
        result.edhNotifications.push(value);
      });
      refSpreadsheetRecordData.push(result);
    });
    return { refSpreadSheet: refSpreadsheetRecordData };
  }, [
    pageState.edited.componentRequirementList,
    pageState.ifDefinitionResult,
    pageState.codes,
  ]);

  const spreadsheetInitialData = useMemo(() => {
    const cmpntPrjList = pageState.edited.cmpntPrjList?.map((e) => e.pjtId);
    const spreadsheetRecordData: SpreadsheetRecordData[] =
      pageState.componentProjectResult.cpntPjtList.map((e) => {
        const data: SpreadsheetRecordData = {
          select:
            cmpntPrjList && e.pjtId ? cmpntPrjList.includes(e.pjtId) : false,
          dipByRgn: e.dipByRgn ?? "",
          cpntId: e.cpntId ?? "",
          cpntPjtTypeName: e.cpntPjtTypeName ?? "",
          inOutTypeName: e.inOutTypeName ?? "",
          prtclTypeName: e.prtcolTypeName ?? "",
          cpntPrjName: e.pjtName ?? "",
          cpntPjtId: e.pjtId ?? "",
        };
        return {
          ...LinkComponentProjectDialogInitialState.spreadSheet.tmplateRow,
          ...data,
        };
      });
    return { spreadSheet: spreadsheetRecordData };
  }, [pageState.edited.cmpntPrjList, pageState.componentProjectResult]);

  const onCellEditedHandle = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const rowIdx: number = event.detail.cell.data.index;
    const isChangedRow: boolean = event.detail.cell._row.isChanged;
    const name = event.detail.name;
    const selectedRow = event.detail.value;
    const isValid = event.detail.cell.valid;
    const cellItem: CellItem = {
      rowIdx: rowIdx,
      col: name,
    };

    const newSpreadsheetRecordData = JSON.parse(
      JSON.stringify(spreadsheetState.spreadSheet),
    ) as SpreadsheetRecordData[];

    newSpreadsheetRecordData[rowIdx].select = !selectedRow.select;
    newSpreadsheetRecordData[rowIdx].updateFlag = isChangedRow;

    setSpreadsheetState({
      ...spreadsheetState,
      spreadSheet: newSpreadsheetRecordData,
    });

    setPageState((prevState) => ({
      ...prevState,
      status: {
        ...prevState.status,
        invalidRecordList: isValid
          ? prevState.status.invalidRecordList.filter(
              (e) => e !== JSON.stringify(cellItem),
            )
          : prevState.status.invalidRecordList.concat(JSON.stringify(cellItem)),
      },
    }));
  };

  const onCellBulkEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const newSpreadsheet = _.cloneDeep(
      spreadsheetState?.spreadSheet,
    ) as SpreadsheetRecordData[];

    event.detail.affectedCells.cells.forEach((item: Cell) => {
      const value: string = item.value;
      const rowIdx: number = item.data.index;
      const currentValue = newSpreadsheet[rowIdx].select ? "true" : "false";
      newSpreadsheet[rowIdx].select = value === "true";
      if (currentValue !== value) newSpreadsheet[rowIdx].updateFlag = true;
    });

    setSpreadsheetState({
      ...spreadsheetState,
      spreadSheet: newSpreadsheet,
    });
  };

  const onClickCancel = () => {
    if (!spreadsheetState) return;
    if (!spreadsheetState.spreadSheet.some((e) => e.updateFlag)) {
      closeModal(false);
    } else {
      showConfirmationDialog({
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        type: "confirm",
        onOkButtonClicked: () => {
          closeModal(false);
        },
      });
    }
  };

  const onClickSave = () => {
    if (!spreadsheetState) return;

    const updateProject = () => {
      resetUserError();
      const cmpntPrjListRequset: ComponentProject[] =
        spreadsheetState.spreadSheet
          .filter((e) => e.select)
          .map((e) => {
            return {
              cmpntPrjId: e.cpntPjtId,
            };
          });
      const request: UpdateProjectRequest = {
        pjtId: pageState.pjtId,
        componentProjectList: cmpntPrjListRequset,
        updateDateTime: pageState.updateDateTime,
      };
      updateProjectRequest(request);
    };
    showConfirmationDialog({
      title: "",
      message: "",
      type: "confirm",
      onOkButtonClicked: updateProject,
    });
  };

  const onListenUpdateProjectResponse = (): void => {
    if (!updateProjectResponse) return;
    if (!spreadsheetState) return;

    if (Array.isArray(updateProjectResponse)) {
      const newSpreadsheetRecordData = JSON.parse(
        JSON.stringify(spreadsheetState.spreadSheet),
      ) as SpreadsheetRecordData[];

      newSpreadsheetRecordData.forEach((row, idx) => {
        row.message = null;
        row.messageVal = "";
        updateProjectResponse.forEach((res) => {
          if (idx === res.idx) {
            row.message = res.message;
            const messageValList: string[] =
              row.message?.map((msg: SpreadsheetMessage) => {
                const displayColumn =
                  pageState.displayColumn.find((e) => e.value === msg.column)
                    ?.label ?? "";
                return `[${displayColumn}: ${msg.messageId}] ${msg.message}`;
              }) ?? [];
            row.messageVal = messageValList.join("\n");
          }
        });
      });
      setSpreadsheetState({
        ...spreadsheetState,
        spreadSheet: newSpreadsheetRecordData,
      });
    } else {
      showToast({
        design: "completion",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00044),
      });
      closeModal(true);
    }
  };
  useEffect(onListenUpdateProjectResponse, [updateProjectResponse]);

  const getContent = () => {
    return (
      <>
        <div
          style={{
            paddingTop: "10px",
            paddingBottom: "30px",
            marginLeft: "30px",
          }}
        >
          <FrVrfFormPanel columns={1}>
            <div>
              <FrVrfButtonPanel align="right">
                <FrVrfButton
                  icon="frIcon:question"
                  theme="tertiary"
                  size="small"
                  tooltipVisible={true}
                  tooltipPosition="below"
                  tooltipContent={MANUAL_LINK.tooltip}
                  style={{
                    "--vrf-tooltip-max-width": "120px",
                    marginRight: "50px",
                  }}
                  onClicked={() => {
                    window.open(MANUAL_LINK.value);
                  }}
                />
              </FrVrfButtonPanel>
              <FrVrfSpreadsheet
                data={refSpreadsheetInitialData}
                templateUrl={REF_TEMPLATE_FILE_URL}
                onInitialized={initRefSpreadsheetState}
                maxHeight={300}
              />
            </div>
            <div style={{ paddingTop: "30px" }}>
              <FrVrfSpreadsheet
                data={spreadsheetInitialData}
                templateUrl={TEMPLATE_FILE_URL}
                onInitialized={initSpreadsheetState}
                onCellEdited={onCellEditedHandle}
                onCellBulkEdited={onCellBulkEdited}
                maxHeight={-70}
              />
            </div>
          </FrVrfFormPanel>
          <FrVrfFooter>
            <FrVrfButton
              content="Cancel"
              theme="secondary"
              size="large"
              onClicked={onClickCancel}
            />
            <FrVrfButton
              content="Save"
              theme="primary"
              size="large"
              disabled={
                pageState.status.invalidRecordList.length !== 0 ||
                !spreadsheetState?.spreadSheet.some((e) => e.updateFlag)
              }
              onClicked={onClickSave}
            />
          </FrVrfFooter>
        </div>
      </>
    );
  };

  return createModal(
    "IF and Component Requirement Definition",
    getContent(),
    onClickCancel,
    "large",
  );
};
